/**
 * Batch workspace PAGE (UX plan §4.4, phase U1) — the artifact-first view of
 * one brief and its creatives. Replaces the dialog-stacked brief workspace:
 * left step rail (Brief → Angles → Creatives → Launch), creative grid canvas,
 * right detail drawer. Rendered by AdCreativeLabPage when `?batch=<briefId>`
 * is present; internally batch = brief 1:1 (decision #1).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { StyledTextField } from '@modules/ui-components';
import {
  useTaskStore,
  useTaskTransition,
  isTerminalTaskStatus,
  ACTIVE_STATUSES,
} from '@modules/background-tasks';
import type { AdCreativeBrief, AdCreativeItem, AdCreativePerformanceFeedback } from '@segmently/schema-models/ad-creative';
import { AD_CREATIVE_BATCH, AD_CREATIVE_INSIGHT } from '../../selectors';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { fetchAdCreativeItem } from '../../services/AdCreativeItemReader';
import {
  creativeLocaleHeadline,
  isSegmentlyOwnedCreativeItem,
} from '../../services/creativeLocaleProjection';
import { GenerationSettingsPopover } from '../common/GenerationSettingsPopover';
import { useAdCreativeGenSettings } from '../../hooks/useAdCreativeGenSettings';
import {
  adCreativeTaskBriefId,
  adCreativeTaskItemId,
  isAdCreativeTask,
  isAdCreativeVideoTask,
} from '../../services/adCreativeTaskScope';
import {
  AdCreativeInsightContextClient,
  INSIGHT_CATEGORY_COLOR,
  parseInsightPath,
  type AdCreativeInsightIndex,
  type AdCreativeInsightIndexEntry,
} from '../../services/AdCreativeInsightContextClient';

type GenerateAdCreativeRequestFeedback = AdCreativePerformanceFeedback;
import {
  deriveStepStatuses,
  getBriefAngles,
  getBriefCampaignId,
  getCreativeBriefGenerationId,
  getItemAngleId,
  getSelectedAngleIds,
  isWinner,
  matchPerformanceRows,
  type AdCreativeAngle,
  type BatchStepKey,
  type ItemPerformance,
} from './batchModel';
import { CreativeMetaSummary } from './CreativeMetaSummary';
import { BatchStepRail } from './BatchStepRail';
import { BriefsStep } from './BriefsStep';
import { BatchCreativeGrid } from './BatchCreativeGrid';
import { CreativeDetailDrawer } from './CreativeDetailDrawer';
import { BatchLaunchStep } from './BatchLaunchStep';

interface AdCreativeBatchWorkspaceProps {
  projectId: string;
  briefId: string;
  client: AdCreativeApiClient;
  step: BatchStepKey | null;
  selectedItemId: string | null;
  onStepChange: (step: BatchStepKey) => void;
  onSelectItem: (itemId: string | null) => void;
  onBack: () => void;
  /** FR5: open the fullscreen storyboard workspace for an item. */
  onOpenStoryboard?: (itemId: string) => void;
  /** Exact locale pinned by a read-only report review handoff. */
  initialLocale?: string;
}

export const AdCreativeBatchWorkspace = React.memo(function AdCreativeBatchWorkspace({
  projectId,
  briefId,
  client,
  step,
  selectedItemId,
  onStepChange,
  onSelectItem,
  onBack,
  onOpenStoryboard,
  initialLocale,
}: AdCreativeBatchWorkspaceProps): React.ReactElement {
  const [brief, setBrief] = useState<AdCreativeBrief | null>(null);
  const [items, setItems] = useState<AdCreativeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [editingBrief, setEditingBrief] = useState(false);
  const [performanceByItemId, setPerformanceByItemId] = useState<Map<string, ItemPerformance>>(new Map());
  // FR4: per-project generation settings (model/effort/budget) — popover in
  // the workspace header; payload rides every text-generation call.
  const genSettings = useAdCreativeGenSettings(projectId);

  const angles = useMemo(() => getBriefAngles(brief), [brief]);
  const winners = useMemo(() => items.filter(isWinner), [items]);

  // Insight index for angle cite blocks (mock 02): the pinned analysis is the
  // brief pin or the analysis the angle citations point at.
  const [insightIndex, setInsightIndex] = useState<AdCreativeInsightIndex>({});
  const pinnedAnalysisId = useMemo(() => {
    const briefPin = (brief as unknown as Record<string, unknown> | null)?.insightsAnalysisId;
    if (typeof briefPin === 'string' && briefPin) return briefPin;
    for (const angle of angles) {
      const parsed = angle.insightRefs?.[0] ? parseInsightPath(angle.insightRefs[0]) : null;
      if (parsed) return parsed.analysisId;
    }
    return '';
  }, [brief, angles]);

  useEffect(() => {
    if (!pinnedAnalysisId || !projectId) {
      setInsightIndex({});
      return;
    }
    let cancelled = false;
    const insightClient = new AdCreativeInsightContextClient(projectId);
    void insightClient.loadInsightIndex(pinnedAnalysisId).then((index) => {
      if (!cancelled) setInsightIndex(index);
    }).catch(() => {
      if (!cancelled) setInsightIndex({});
    });
    return () => {
      cancelled = true;
    };
  }, [pinnedAnalysisId, projectId]);

  // Uncovered theses, strongest first. The strip quotes the top-2; the FR3
  // seed checklist (mock 04) lets the user edit the pre-checked set from a
  // longer list before generating. On a manual-selection brief only SELECTED
  // theses are offered — a seed outside the selection could never be honored
  // (the backend would surface it as unresolvedSeeds, FR3-PRE-003).
  const uncoveredInsights = useMemo(() => {
    const cited = new Set(
      angles.flatMap((angle) => angle.insightRefs ?? [])
        .map((ref) => parseInsightPath(ref)?.insightId)
        .filter(Boolean) as string[],
    );
    const briefSelection = brief as { selectedInsightIds?: string[]; insightSelectionMode?: string } | null;
    const manualSet = briefSelection?.insightSelectionMode === 'manual'
      && Array.isArray(briefSelection.selectedInsightIds)
      && briefSelection.selectedInsightIds.length > 0
      ? new Set(briefSelection.selectedInsightIds.map(String))
      : null;
    return Object.entries(insightIndex)
      .filter(([insightId]) => !cited.has(insightId) && (!manualSet || manualSet.has(insightId)))
      .map(([insightId, entry]) => ({ insightId, ...entry }))
      .sort((a, b) => b.cumulativeWeight - a.cumulativeWeight)
      .slice(0, 8);
  }, [angles, brief, insightIndex]);
  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId) ?? null,
    [items, selectedItemId],
  );
  // FR8: briefs_first batches with drafts and no items land on the review stage.
  const creativeBriefGenerationId = getCreativeBriefGenerationId(brief);
  const activeStep: BatchStepKey = step
    ?? (items.length > 0 ? 'creatives' : creativeBriefGenerationId ? 'briefs' : 'brief');

  const refresh = useCallback(async () => {
    if (!projectId || !briefId) return;
    try {
      const [briefResponse, itemsResponse] = await Promise.all([
        client.getBrief(projectId, briefId),
        client.listItems(projectId, { briefId, limit: 100, abortKey: 'batch-workspace-items' }),
      ]);
      // A newer navigation superseded this refresh — drop it silently.
      if ((itemsResponse as { aborted?: boolean }).aborted) return;
      if (briefResponse.success && briefResponse.data?.brief) {
        setBrief(briefResponse.data.brief);
      } else if (briefResponse.error) {
        setError(briefResponse.error);
      }
      if (itemsResponse.success && itemsResponse.data) {
        setItems(itemsResponse.data.items.filter(isSegmentlyOwnedCreativeItem));
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    }
  }, [briefId, client, projectId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    void refresh().finally(() => setLoading(false));
  }, [refresh]);

  const refreshItem = useCallback(async (itemId: string) => {
    try {
      // Single-doc read: a task completion must not refetch the 100-item list.
      const item = await fetchAdCreativeItem(projectId, itemId);
      if (!item || !isSegmentlyOwnedCreativeItem(item)) return;
      setItems((current) => (current.some((candidate) => candidate.id === item.id)
        ? current.map((candidate) => (candidate.id === item.id ? item : candidate))
        : [...current, item]));
    } catch {
      // Exceptional path (rules/network): fall back to the list fetch so the
      // card never freezes on stale state.
      const response = await client.listItems(projectId, { briefId, limit: 100, abortKey: 'batch-workspace-items' });
      if (response.success && response.data) setItems(response.data.items.filter(isSegmentlyOwnedCreativeItem));
    }
  }, [briefId, client, projectId]);

  // Task lifecycle (FR0-I1): transitions of this brief's tasks refresh the
  // workspace once; video-generation transitions patch only the affected item.
  useTaskTransition({
    filter: isAdCreativeTask,
    onTransition: (task) => {
      if (!isTerminalTaskStatus(task.status)) return;
      const taskBriefId = adCreativeTaskBriefId(task);
      if (taskBriefId && taskBriefId !== briefId) return;
      if (isAdCreativeVideoTask(task)) {
        const itemId = adCreativeTaskItemId(task);
        if (itemId) {
          void refreshItem(itemId);
          return;
        }
      }
      void refresh();
    },
  });

  // Safety net (FR0-PRE-003): one slow interval while a task runs; torn down
  // on completion/unmount. The only timer this surface is allowed to own.
  const hasRunningAdCreativeTask = useTaskStore((state) =>
    Object.values(state.tasks).some((task) => isAdCreativeTask(task) && ACTIVE_STATUSES.includes(task.status)));
  useEffect(() => {
    if (!hasRunningAdCreativeTask) return undefined;
    const interval = window.setInterval(() => {
      void refresh();
    }, 60_000);
    return () => window.clearInterval(interval);
  }, [hasRunningAdCreativeTask, refresh]);

  // E5 feedback join (U7): once creatives are live in Meta, pull the funnel
  // warehouse CAC rows and match them by metaAdId for TRUE per-creative
  // conversions. Silent no-op when the warehouse has no data yet.
  const launchedAdIds = useMemo(
    () => items.map((entry) => entry.providerAdId).filter(Boolean).join(','),
    [items],
  );
  useEffect(() => {
    if (!launchedAdIds) return;
    let cancelled = false;
    void client.getCacByCampaign(projectId).then((response) => {
      if (cancelled) return;
      const rows = response.data?.campaigns
        ?? (response as { result?: { campaigns?: Array<Record<string, unknown>> } }).result?.campaigns
        ?? response.campaigns
        ?? [];
      if (rows.length) setPerformanceByItemId(matchPerformanceRows(items, rows));
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, projectId, launchedAdIds]);

  const runAction = useCallback(async (successMessage: string, action: () => Promise<void>) => {
    setRunning(true);
    setError(null);
    setStatus(null);
    try {
      await action();
      setStatus(successMessage);
      // One immediate refresh picks up rows the action just created; the task
      // transition subscription owns everything after that (no timer ladders).
      void refresh();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setRunning(false);
    }
  }, [refresh]);

  const applyItemUpdate = useCallback((item: AdCreativeItem) => {
    setItems((current) => current.map((candidate) => (candidate.id === item.id ? item : candidate)));
  }, []);

  const toggleWinner = useCallback(async (item: AdCreativeItem, winner: boolean) => {
    setItems((current) => current.map((candidate) => (candidate.id === item.id
      ? { ...candidate, review: { ...(candidate.review ?? {}), approvalStage: winner ? 'approved' : 'pending' } }
      : candidate)));
    const response = await client.updateItem(projectId, item.id, {
      review: { approvalStage: winner ? 'approved' : 'pending' },
    });
    if (!response.success) {
      setError(response.error || 'Failed to update winner state');
      await refresh();
    } else if (response.data?.item) {
      applyItemUpdate(response.data.item);
    }
  }, [applyItemUpdate, client, projectId, refresh]);

  const generateVariants = useCallback(async (options: { templateIds?: string[]; note: string }) => {
    if (!brief) return;
    await runAction('Generation task started', async () => {
      const templateIds = options.templateIds?.length
        ? options.templateIds
        : brief.selectedTemplateIds ?? [];
      const response = await client.generateTask({
        projectId,
        briefId: brief.id,
        campaignId: getBriefCampaignId(brief),
        networkId: brief.networkId,
        templateIds,
        variantCount: templateIds.length || 1,
        generateAssets: true,
        designCollectionId: brief.designCollectionId,
        selectedDesignPresetIds: brief.selectedDesignPresetIds,
        generation: genSettings.requestPayload,
        feedback: options.note
          ? ({ nextPromptFeedback: options.note } as GenerateAdCreativeRequestFeedback)
          : undefined,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to start generation');
      }
    });
  }, [brief, client, projectId, runAction]);

  const generateAngles = useCallback(async (seedInsightIds?: string[]) => {
    if (!brief) return;
    await runAction('Angle generation started', async () => {
      const response = await client.generateAngles(projectId, getBriefCampaignId(brief), brief.id, {
        count: seedInsightIds?.length || 4,
        mode: angles.length ? 'append' : 'replace',
        seedInsightIds,
        generation: genSettings.requestPayload,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to generate angles');
      }
    });
  }, [angles.length, brief, client, projectId, runAction]);

  const runResearch = useCallback(async () => {
    if (!brief) return;
    await runAction('Research task started', async () => {
      const response = await client.createResearchRun(projectId, getBriefCampaignId(brief), brief.id, {});
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to start research');
      }
    });
  }, [brief, client, projectId, runAction]);

  // Stable grid/drawer callbacks + memoized nodes (FR0-I2): the memoized grid
  // and drawer must not re-render from recreated arrow props.
  const handleOpenItem = useCallback((item: AdCreativeItem) => onSelectItem(item.id), [onSelectItem]);
  const handleToggleWinner = useCallback(
    (item: AdCreativeItem, winner: boolean) => void toggleWinner(item, winner),
    [toggleWinner],
  );
  const handleCloseDrawer = useCallback(() => onSelectItem(null), [onSelectItem]);
  const handleRegenerateImage = useCallback(async (item: AdCreativeItem) => {
    await generateVariants({
      templateIds: item.templateId ? [item.templateId] : undefined,
      note: 'Regenerate the visual for this template with the same message.',
    });
  }, [generateVariants]);
  const handleVariateCopy = useCallback(async (item: AdCreativeItem) => {
    await generateVariants({
      templateIds: item.templateId ? [item.templateId] : undefined,
      note: 'Keep the visual direction; produce an alternative copy variant.',
    });
  }, [generateVariants]);
  const handleDrawerMakeVideo = useCallback(async (item: AdCreativeItem) => {
    // FR2: video variant of an image creative — restrict to the brief's
    // video-capable templates when identifiable, else let the brief defaults
    // apply; the note steers the writer either way.
    const videoTemplateIds = (brief?.selectedTemplateIds ?? [])
      .filter((templateId) => /reel|video|story/i.test(templateId));
    await generateVariants({
      templateIds: videoTemplateIds.length > 0 ? videoTemplateIds : undefined,
      note: `Create a video (reel) variant of creative "${creativeLocaleHeadline(item, item.defaultLanguage, 'strict')}" reusing its message.`,
    });
  }, [brief?.selectedTemplateIds, generateVariants]);
  const handleLaunchItemsUpdated = useCallback(() => void refresh(), [refresh]);
  const handleRetryItem = useCallback(async (item: AdCreativeItem) => {
    await generateVariants({
      templateIds: item.templateId ? [item.templateId] : undefined,
      note: 'Retry this creative after a failed generation.',
    });
  }, [generateVariants]);
  const renderLightboxMeta = useCallback((item: AdCreativeItem) => (
    <CreativeMetaSummary
      item={item}
      angle={angles.find((entry) => entry.id === getItemAngleId(item)) ?? null}
    />
  ), [angles]);
  // Phase pill for generating cards: string|null selector — re-renders only
  // when the phase KEY changes, not on every heartbeat write.
  const generatingPhase = useTaskStore((state) => {
    for (const task of Object.values(state.tasks)) {
      if (String(task.taskType ?? '') !== 'ad-creative-generation') continue;
      if (!ACTIVE_STATUSES.includes(task.status)) continue;
      const taskBriefId = adCreativeTaskBriefId(task);
      if (taskBriefId && taskBriefId !== briefId) continue;
      return task.currentPhaseKey ?? null;
    }
    return null;
  });
  const selectedAngle = useMemo(
    () => (selectedItem?.angleId ? angles.find((entry) => entry.id === selectedItem.angleId) ?? null : null),
    [angles, selectedItem],
  );
  const emptyAction = useMemo(() => (
    <Button
      variant="contained"
      size="small"
      startIcon={<AutoAwesomeIcon />}
      disabled={running || !brief}
      onClick={() => void generateVariants({ note: '' })}
    >
      Generate batch
    </Button>
  ), [brief, generateVariants, running]);

  const steps = deriveStepStatuses({
    brief,
    angles,
    items,
    winnersCount: winners.length,
    launched: items.some((item) => Boolean(item.providerAdId)),
  });

  if (loading && !brief) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Box data-testid={AD_CREATIVE_BATCH.workspace}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button
          data-testid={AD_CREATIVE_BATCH.back}
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={onBack}
        >
          Batches
        </Button>
        <Typography variant="h6" data-testid={AD_CREATIVE_BATCH.title} sx={{ fontWeight: 700, flex: 1, minWidth: 200 }} noWrap>
          {brief?.title ?? 'Batch'}
        </Typography>
        <GenerationSettingsPopover
          projectId={projectId}
          settings={genSettings.settings}
          onUpdate={genSettings.update}
          onReset={genSettings.reset}
        />
        <Button
          data-testid={AD_CREATIVE_BATCH.moreVariants}
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={running || !brief}
          onClick={() => void generateVariants({ note: '' })}
        >
          More variants
        </Button>
        <Button
          data-testid={AD_CREATIVE_BATCH.continueToLaunch}
          size="small"
          variant="contained"
          startIcon={<RocketLaunchIcon />}
          disabled={winners.length === 0}
          onClick={() => onStepChange('launch')}
        >
          {`Continue to Launch${winners.length ? ` (${winners.length} selected)` : ''}`}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {status && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setStatus(null)}>{status}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <BatchStepRail
          steps={steps}
          activeStep={activeStep}
          brief={brief}
          onSelectStep={onStepChange}
          onEditBrief={() => {
            setEditingBrief(true);
            onStepChange('brief');
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {activeStep === 'brief' && (
            <BriefStep
              brief={brief}
              editing={editingBrief}
              running={running}
              onEdit={() => setEditingBrief(true)}
              onCancelEdit={() => setEditingBrief(false)}
              onSave={async (patch) => {
                if (!brief) return;
                await runAction('Brief updated', async () => {
                  const response = await client.updateBrief(projectId, brief.id, patch);
                  if (!response.success || !response.data?.brief) {
                    throw new Error(response.error || 'Failed to update brief');
                  }
                  setBrief(response.data.brief);
                });
                setEditingBrief(false);
              }}
              onRunResearch={() => void runResearch()}
            />
          )}

          {activeStep === 'angles' && (
            <AnglesStep
              angles={angles}
              selectedAngleIds={getSelectedAngleIds(brief)}
              running={running}
              insightIndex={insightIndex}
              uncoveredInsights={uncoveredInsights}
              onGenerate={() => void generateAngles()}
              onAngleFromInsight={(insightIds) => void generateAngles(insightIds)}
            />
          )}

          {activeStep === 'briefs' && creativeBriefGenerationId && (
            <BriefsStep
              projectId={projectId}
              client={client}
              briefId={briefId}
              generationId={creativeBriefGenerationId}
              onGenerated={() => void refresh()}
              onOpenStoryboard={(itemId) => onOpenStoryboard?.(itemId)}
            />
          )}

          {activeStep === 'creatives' && (
            <BatchCreativeGrid
              items={items}
              angles={angles}
              performanceByItemId={performanceByItemId}
              selectedItemId={selectedItemId}
              onOpenItem={handleOpenItem}
              onToggleWinner={handleToggleWinner}
              onMakeVideo={handleOpenItem}
              onOpenStoryboard={(item) => onOpenStoryboard?.(item.id)}
              onRetryItem={handleRetryItem}
              renderLightboxMeta={renderLightboxMeta}
              generatingPhase={generatingPhase}
              emptyAction={emptyAction}
            />
          )}

          {activeStep === 'launch' && brief && (
            <BatchLaunchStep
              projectId={projectId}
              client={client}
              brief={brief}
              winners={winners}
              allItems={items}
              onItemsUpdated={handleLaunchItemsUpdated}
            />
          )}
        </Box>

        <CreativeDetailDrawer
          projectId={projectId}
          client={client}
          item={selectedItem}
          angle={selectedAngle}
          insightIndex={insightIndex}
          onClose={handleCloseDrawer}
          onItemUpdated={applyItemUpdate}
          onRefreshItem={refreshItem}
          onRegenerateImage={handleRegenerateImage}
          onVariateCopy={handleVariateCopy}
          onMakeVideo={handleDrawerMakeVideo}
          onOpenStoryboard={onOpenStoryboard}
          initialLocale={initialLocale}
        />
      </Stack>
    </Box>
  );
});

function BriefStep({
  brief,
  editing,
  running,
  onEdit,
  onCancelEdit,
  onSave,
  onRunResearch,
}: {
  brief: AdCreativeBrief | null;
  editing: boolean;
  running: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
  onRunResearch: () => void;
}): React.ReactElement {
  const [title, setTitle] = useState('');
  const [objective, setObjective] = useState('');

  useEffect(() => {
    setTitle(brief?.title ?? '');
    setObjective(brief?.objective ?? '');
  }, [brief?.title, brief?.objective, editing]);

  if (!brief) {
    return <Typography variant="body2" color="text.secondary">Brief not found.</Typography>;
  }

  return (
    <Stack spacing={1.5} data-testid={AD_CREATIVE_BATCH.briefStep}>
      {!editing ? (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{brief.title}</Typography>
          {brief.objective && (
            <Typography variant="body2" color="text.secondary">Goal: {brief.objective}</Typography>
          )}
          {brief.productContext?.offerPromise ? (
            <Typography variant="body2" color="text.secondary">
              Offer: {String(brief.productContext.offerPromise)}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button size="small" variant="outlined" onClick={onEdit}>Edit brief</Button>
            <Button
              size="small"
              data-testid={AD_CREATIVE_BATCH.research}
              disabled={running}
              onClick={onRunResearch}
            >
              Enrich with research
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Stack spacing={1.5}>
            <StyledTextField label="Title" size="small" fullWidth value={title} onChange={(value) => setTitle(String(value))} />
            <StyledTextField label="Goal / objective" size="small" fullWidth value={objective} onChange={(value) => setObjective(String(value))} />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={running}
                onClick={() => void onSave({ title, objective })}
              >
                Save
              </Button>
              <Button size="small" onClick={onCancelEdit}>Cancel</Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

function AnglesStep({
  angles,
  selectedAngleIds,
  running,
  insightIndex,
  uncoveredInsights,
  onGenerate,
  onAngleFromInsight,
}: {
  angles: AdCreativeAngle[];
  selectedAngleIds: string[];
  running: boolean;
  insightIndex: AdCreativeInsightIndex;
  uncoveredInsights: Array<{ insightId: string } & AdCreativeInsightIndexEntry>;
  onGenerate: () => void;
  onAngleFromInsight: (insightIds: string[]) => void;
}): React.ReactElement {
  // FR3 seed checklist (mock 04): pre-checked top-2 uncovered, user-editable.
  const [seedAnchor, setSeedAnchor] = useState<HTMLElement | null>(null);
  const [checkedSeeds, setCheckedSeeds] = useState<string[]>([]);
  const openSeedChecklist = (event: React.MouseEvent<HTMLElement>) => {
    setCheckedSeeds(uncoveredInsights.slice(0, 2).map((entry) => entry.insightId));
    setSeedAnchor(event.currentTarget);
  };
  return (
    <Stack spacing={1.5} data-testid={AD_CREATIVE_BATCH.anglesStep}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {angles.length ? `Angles (${angles.length})` : 'Angles'}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={running ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
          data-testid={AD_CREATIVE_BATCH.anglesGenerate}
          disabled={running}
          onClick={onGenerate}
        >
          {angles.length ? 'More angles' : 'Generate angles'}
        </Button>
      </Stack>
      {angles.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Angles are message hypotheses the batch multiplies across formats. Generate them once — every creative references one.
        </Typography>
      )}
      {angles.map((angle) => {
        const citedPath = angle.insightRefs?.[0];
        const cited = citedPath ? parseInsightPath(citedPath) : null;
        const citedEntry = cited ? insightIndex[cited.insightId] : undefined;
        return (
          <Box
            key={angle.id}
            data-testid={AD_CREATIVE_BATCH.angleRow(angle.id)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>{angle.label}</Typography>
              {angle.audienceRef?.labelSnapshot && (
                <Chip
                  size="small"
                  variant="outlined"
                  data-testid={AD_CREATIVE_INSIGHT.angleAudience(angle.id)}
                  label={`Audience: ${angle.audienceRef.labelSnapshot}`}
                />
              )}
              {(selectedAngleIds.includes(angle.id) || angle.status === 'selected') && (
                <Chip size="small" color="primary" variant="outlined" label="Selected" />
              )}
            </Stack>
            {angle.thesis && (
              <Typography variant="body2" color="text.secondary">{angle.thesis}</Typography>
            )}
            {/* Cited insight provenance (mock 02) — or an honest "invented" label. */}
            {citedPath && cited ? (
              <Box
                data-testid={AD_CREATIVE_INSIGHT.angleCite(angle.id)}
                sx={{ mt: 1, pl: 1.25, py: 0.75, borderLeft: 3, borderColor: 'info.main', bgcolor: 'action.hover', borderRadius: '0 8px 8px 0' }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color={INSIGHT_CATEGORY_COLOR[cited.category] ?? 'default'} label={cited.category} sx={{ height: 18, fontSize: 10 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {citedPath}
                  </Typography>
                </Stack>
                {citedEntry?.topQuote && (
                  <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}>
                    “{citedEntry.topQuote}”
                  </Typography>
                )}
                {citedEntry && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {`${citedEntry.actorCount} actors · weight ${citedEntry.cumulativeWeight}`}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                invented — not grounded in research
              </Typography>
            )}
          </Box>
        );
      })}
      {/* Coverage strip (FP-9): strong theses no angle works yet. */}
      {uncoveredInsights.length > 0 && (
        <Alert
          severity="info"
          icon={false}
          data-testid={AD_CREATIVE_INSIGHT.coverageStrip}
          action={(
            <Button
              size="small"
              variant="outlined"
              data-testid={AD_CREATIVE_INSIGHT.angleFromInsight}
              disabled={running}
              onClick={openSeedChecklist}
            >
              + Angle from insight ▾
            </Button>
          )}
        >
          <b>{`${Math.min(uncoveredInsights.length, 2)} strong insight${Math.min(uncoveredInsights.length, 2) === 1 ? ' has' : 's have'} no angle yet: `}</b>
          {uncoveredInsights.slice(0, 2).map((entry) => `[${entry.category}] “${entry.thesis}”`).join(' · ')}
        </Alert>
      )}
      {/* FR3 — seed checklist popover (mock 04). */}
      <Popover
        open={Boolean(seedAnchor)}
        anchorEl={seedAnchor}
        onClose={() => setSeedAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 440, maxHeight: 440, display: 'flex', flexDirection: 'column' } } }}
      >
        <Stack data-testid={AD_CREATIVE_INSIGHT.seedChecklist} sx={{ minHeight: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, p: 1.5, pb: 0.75 }}>
            Build angles on these theses
          </Typography>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5 }}>
            {uncoveredInsights.map((entry) => (
              <Stack
                key={entry.insightId}
                direction="row"
                spacing={0.75}
                data-testid={AD_CREATIVE_INSIGHT.seedRow(entry.insightId)}
                sx={{ alignItems: 'flex-start', py: 0.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Checkbox
                  size="small"
                  sx={{ p: 0.25 }}
                  checked={checkedSeeds.includes(entry.insightId)}
                  onChange={() => setCheckedSeeds((current) => (current.includes(entry.insightId)
                    ? current.filter((id) => id !== entry.insightId)
                    : [...current, entry.insightId]))}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Chip
                      size="small"
                      color={INSIGHT_CATEGORY_COLOR[entry.category] ?? 'default'}
                      label={entry.category}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {`${entry.actorCount} actors · weight ${entry.cumulativeWeight}`}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
                    {entry.thesis}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
          <Stack direction="row" sx={{ p: 1.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="contained"
              data-testid={AD_CREATIVE_INSIGHT.seedGenerate}
              disabled={checkedSeeds.length === 0 || running}
              onClick={() => {
                setSeedAnchor(null);
                onAngleFromInsight(checkedSeeds);
              }}
            >
              {`Generate ${checkedSeeds.length} angle${checkedSeeds.length === 1 ? '' : 's'}`}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
}
